import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { Loader } from 'semantic-ui-react';

type ImageWithLoaderProps = {
  src: string;
  alt?: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  onClick?: (event: React.MouseEvent) => void;
  loaderSize?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  showLoaderBackground?: boolean;
} & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

function ImageWithLoader({
  src,
  alt = '',
  className = '',
  onLoad,
  onError,
  onClick,
  loaderSize = 'medium',
  showLoaderBackground = true,
  ...rest
}: ImageWithLoaderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Reset loading state when src changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const handleClick = (event: React.MouseEvent) => {
    if (!isLoading && !hasError) {
      onClick?.(event);
    }
  };

  return (
    <button
      className={classNames('relative inline-block', className)}
      onClick={handleClick}
      type="button"
    >
      {/* Loading state */}
      {isLoading && (
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            showLoaderBackground ? 'bg-white/80' : ''
          }`}
        >
          <Loader size={loaderSize} active />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500">
          <div className="text-center">
            <div className="text-2xl mb-2">📷</div>
            <div className="text-sm">Failed to load image</div>
          </div>
        </div>
      )}

      {/* Image */}
      {/* eslint-disable react/jsx-props-no-spreading */}
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          opacity: isLoading || hasError ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
        }}
        {...rest}
      />
    </button>
  );
}

export default ImageWithLoader;
